<template>
    <div>
        <div>
            <div class="form-actions flex justify-between">
                <div class="flex vx-col-action">
                    <vs-input icon-pack="feather" icon="icon-search" placeholder="Type here..." v-model="globalSearchTerm" class="is-label-placeholder searchBar-input" id="filter-text-box" v-on:input="onFilterTextBoxChanged()" />
                    <span class="br-line mr-2 ml-2"></span>
                </div>

                <div class="float-right flex">
                    <vs-chip class="chip-selected-rows" transparent closable color="primary" @click="deselectAllSelectedRows()">
                        {{ this.gridApi ? this.gridApi.getSelectedNodes().length : 0 }} Rows Selected
                    </vs-chip>
                    <vx-tooltip :text="
              !flags['selectedAll'][this.currentPage - 1]
                ? 'Select All'
                : 'Deselect All'
            " position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" :icon="
                flags['selectedAll'][this.currentPage - 1]
                  ? 'icon-check-square'
                  : 'icon-square'
              " @click="toggleSelectAll()"></vs-button>
                    </vx-tooltip>

                    <span class="br-line-long mr-2 ml-2 h-60"></span>

                    <vx-tooltip v-if="isLocalRule" text="Add promo" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-plus" @click="addPromo()"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip v-if="isLocalEditRule" text="Edit promo" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-edit" @click="editPromo()" :disabled="this.selectedRowCount == 0"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip v-if="isLocalRule" text="Duplicate promo" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-copy" @click="duplicatePromo()" :disabled="this.selectedRowCount !== 1"></vs-button>
                    </vx-tooltip>

                    <span class="br-line-long mr-2 ml-2 h-60"></span>

                    <vx-tooltip text="Download list" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-download" @click="downloadFilteredCsv()"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip text="Reset Filters" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-refresh-ccw" @click="refreshPageWithFilters()"></vs-button>
                    </vx-tooltip>
                </div>
            </div>

            <div id="grid-loader" class="vx-card vs-con-loading__container">
                <ag-grid-vue :gridOptions="gridOptions" class="ag-theme-material w-100 mb-4 ag-grid-table" :columnDefs="columnDefs" :defaultColDef="defaultColDef" :components="components" :rowData="tableData" rowSelection="multiple" :enableRangeSelection="true" :suppressCopyRowsToClipboard="true" :suppressDragLeaveHidesColumns="true" :animateRows="false" :floatingFilter="true" :pagination="false" :suppressPaginationPanel="true" :suppressRowClickSelection="true" :rowClassRules="rowClassRules" :statusBar="statusBar" :sideBar="sideBar" :rowHeight="30" :headerHeight="30" :floatingFiltersHeight="35" :checkboxSelection="true" :enableBrowserTooltips="true" @filter-changed="onFilterChanged" @sort-changed="onSortChanged" @row-selected="onRowSelected" @column-resized="onColumnResized">
                </ag-grid-vue>
                <div class="vx-row py-5">
                    <div class="vx-col w-full">
                        <vs-pagination :total="totalPages" :max="maxPageNumbers" class="custom-pagination" v-model="currentPage" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import "@/assets/scss/radix/extraComponents/agGridStyleOverride.scss";

import CellRendererStatus from "@/components/ag-grid-tables/actions/CellRendererStatus.vue";
import EditInternalPromo from "@/components/ag-grid-tables/promos/EditInternalPromo.vue";

import CellRendererAgreementList from "@/components/ag-grid-tables/actions/CellRendererAgreementList.vue";

// Maps ag-grid column ids (floating filters) to the flat query params the
// backend's LocalPromoRepository::applyLocalPromoFilters understands.
// Client Name / Registrar Name / AM / TLD: field stays name-based (cell
// rendering untouched), and selected names are resolved to id(s) in
// buildFilterParams() ("set-id") right before the request goes out.
// sortKey is only needed where the backend's sort_by key differs from the
// filter param above (e.g. filtering resellers/registrars is by id, sorting
// is by the joined name) — omitted entries fall back to `param` below.
// facetKey names the column's option list in the /local-promos/filter-options
// response. Every Set Filter column has one, and all of them are refetched
// after any filter change, so each dropdown only offers values that still
// match under the other filters in force (see syncFilterOptions).
const FILTER_PARAM_MAP = {
    "new_reseller.name": { param: "reseller_id", type: "set-id", optionsKey: "resellerOptions", nameKey: "name", sortKey: "reseller_name", facetKey: "resellers" },
    "new_reseller.unique_registrar.name": { param: "registrar_id", type: "set-id", optionsKey: "registrarOptions", nameKey: "name", sortKey: "registrar_name", facetKey: "registrars" },
    "am.name": { param: "am_id", type: "set-id", optionsKey: "amOptions", nameKey: "name", sortKey: "am_name", facetKey: "ams" },
    "tld.tld_name": { param: "tld_id", type: "set-id", optionsKey: "tldOptions", nameKey: "tld_name", sortKey: "tld", facetKey: "tlds" },
    "status": { param: "status", type: "set", facetKey: "statuses" },
    "type": { param: "type", type: "set", facetKey: "types" },
    "start_date": { param: "start_date", type: "date" },
    "end_date": { param: "end_date", type: "date" },
    "period": { param: "period", type: "set", facetKey: "periods" },
    "retail_price": { param: "retail_price", type: "text" },
    "effective_first": { param: "effective_first", type: "text" },
    // Sent as tag NAME, not id — promo_tags/custom_tags rows aren't unique
    // by name (many rows can share one tag_name), so resolving to a single
    // id client-side would silently drop matches. The backend joins and
    // matches by name instead (see LocalPromoRepository::applyLocalPromoFilters).
    // Both option lists carry ag-grid's null "(Blanks)" entry for promos
    // without that tag; blankValue is the token the backend matches as
    // "promo has no such tag" (LocalPromoRepository::BLANK_TAG_OPTION).
    "promotag.tag_name": { param: "promo_tag", type: "set", blankValue: "__blank__", facetKey: "promoTags" },
    "customtag.tag_name": { param: "custom_tag", type: "set", blankValue: "__blank__", facetKey: "customTags" },
    "payment_mode": { param: "payment_mode", type: "set", facetKey: "paymentModes" },
    "list_based": { param: "list_based", type: "set", valuesMap: { "Yes": "yes", "No": "no" }, facetKey: "listBasedOptions" },
    "list_frequency": { param: "list_frequency", type: "set", facetKey: "listFrequencies" },
    "rebates_status": { param: "rebates_status", type: "set", facetKey: "rebatesStatuses" },
    // "dropbox_detail" (Agreements) has no meaningful raw value to filter on
    // (the cell renders a "View" popup, not the underlying data) — left
    // unmapped on purpose, same as before pagination.
};

export default {
    name: "InternalPromoListComponent",
    components: {
        AgGridVue,
        CellRendererStatus,
        EditInternalPromo,
        CellRendererAgreementList,
    },
    props: {
        expired: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedRowCount: "",
            rejectedPromo: false,
            activePrompt: false,
            reject_comments: "",
            autoGroupColumnDef: null,
            gridOptions: {},
            maxPageNumbers: 7,
            gridApi: null,
            currentPage: 1,
            perPage: 18,
            totalRows: 0,
            totalPagesFromApi: 0,
            searchDebounceTimer: null,
            suppressGridEvents: false,
            // Name/id lookup source for reseller/registrar/am/tld filters —
            // all come straight from the store (fetched once at login),
            // never refetched here. Used only to translate what the user
            // checks in the (unchanged) name-based filter boxes into ids
            // before the request goes out; row/cell data is untouched.
            resellerOptions: [],
            registrarOptions: [],
            tldOptions: [],
            amOptions: [],
            // Current option list per Set Filter column, keyed by
            // FILTER_PARAM_MAP's facetKey — what /local-promos/filter-options
            // returned for the filters in force when last fetched
            // (facetsParamsKey), i.e. only values that still match.
            facets: {},
            facetsParamsKey: null,
            facetsRequestId: 0,
            defaultColDef: {
                sortable: true,
                resizable: true,
                suppressMenu: true,
                suppressMovable: false,
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                },
            },
            rowClassRules: null,
            statusBar: null,
            sideBar: null,
            columnDefs: null,
            tableData: null,
            selectedPromos: [],
            // Cell Renderer Components
            components: {
                CellRendererStatus,
            },
            flags: {
                disabledAction: true,
                // selectedAll: false,
                selectedAll: [],
            },
            globalSearchTerm: "",
        };
    },
    watch: {
        windowWidth(val) {
            // Toggle colummn movable
            this.toggleColumnMovable(val);
            // Toggle colummn pinning
            this.toggleColumnPinned(val);
        },
        currentPage(val, oldVal) {
            if (val !== oldVal) {
                this.populateTableData();
            }
        },
    },
    computed: {
        // Separate persistence per tab instance ("Internal Promos" vs
        // "Expired > Internal"), since Lists.vue mounts two independent
        // instances of this component.
        storageKey() {
            return this.expired
                ? "internalPromoListFilters_expired"
                : "internalPromoListFilters";
        },
        windowWidth: function () {
            return this.$store.state.windowWidth;
        },
        // user_role() {
        //     return JSON.parse(localStorage.getItem("userDetails")).role;
        // },
        user_id() {
            return JSON.parse(localStorage.getItem("userDetails")).id;
        },
        status() {
            // console.log(data && data ? data.status:'');
            return event.node;
        },
        paginationPageSize() {
            return this.perPage;
        },
        totalPages() {
            if (this.totalPagesFromApi) {
                return this.totalPagesFromApi;
            }

            return this.totalRows ? Math.ceil(this.totalRows / this.perPage) : 0;
        },
        validComment() {
            return this.reject_comments.length > 0;
        },
        rejectTitle() {
            return "Reject Selected Promos?";
        },
        yesText() {
            return "Yes";
        },
        noText() {
            return "No";
        },
        isLocalRule() {
            return this.$acl.check('isSuperAdmin') || this.$acl.check('isChannelAdmin') || this.$acl.check('isBIAdmin') || this.$acl.check('isChannelUser');
        },
        isLocalEditRule() {
            return this.$acl.check('isSuperAdmin') || this.$acl.check('isChannelAdmin') || this.$acl.check('isBIAdmin') || this.$acl.check('isChannelUser') || this.$acl.check('isBIUser');
        },
        isBIUserRole() {
            return this.$acl.check('isSuperAdmin') || this.$acl.check('isChannelAdmin') || this.$acl.check('isBIAdmin') || this.$acl.check('isBIUser');
        }
    },
    methods: {
        onPageSizeChanged(newPageSize) {
            this.perPage = Number(newPageSize) || 15;
            if (this.currentPage === 1) {
                this.populateTableData();
            } else {
                this.currentPage = 1;
            }
        },
        onRowSelected(event) {
            var getStatus = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data)
                .map((x) => x.status);
            getStatus.includes("rejected")
                ? (this.rejectedPromo = true)
                : (this.rejectedPromo = false);
            if (event.node.selected) {
                // Push to selectedPromos
                // console.log('lenght', this.gridApi.getSelectedNodes().length);
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
                if (this.selectedRowCount > 30) {
                    event.node.setSelected(false);

                    this.$vs.notify({
                        time: 5000,
                        color: "warning",
                        title: "Warning",
                        text: "You may select only upto 30 rows",
                    });
                }
            } else {
                // Remove from selectedPromos
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
            }
        },
        onFilterChanged() {
            if (this.suppressGridEvents) {
                return;
            }

            this.deselectAllSelectedRows();
            this.scheduleReload();
        },
        onSortChanged() {
            if (this.suppressGridEvents) {
                return;
            }

            this.scheduleReload();
        },
        // The one debounced path everything funnels through after the
        // search text, a column filter or the sort changes: refresh the
        // option lists if the filter set changed (a no-op otherwise, see
        // syncFilterOptions), then reload the rows. Shared on purpose —
        // these events arrive back to back (Reset Filters clears filters
        // and sort in one go), and separate timers let the last one cancel
        // the option refresh the earlier one had queued.
        scheduleReload() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.syncFilterOptions().then((selectionChanged) => {
                    // A narrowed option list dropped part of what was checked
                    // somewhere, and pushing that back into the grid re-fires
                    // filter-changed — the reload runs from that pass instead,
                    // with the final filter state.
                    if (selectionChanged) {
                        return;
                    }

                    this.persistFilterState();
                    if (this.currentPage === 1) {
                        this.populateTableData();
                    } else {
                        this.currentPage = 1;
                    }
                });
            }, 350);
        },
        // NOTE: If logged in user is Erica, default the AM filter to her own
        // name, don't apply a default filter for any other logged in user.
        applyAmExclusionFilter() {
            if (!this.gridApi || !this.isAMExclusionActive()) {
                return;
            }

            const exclusionName = (process.env.VUE_APP_AM_EXCLUSION || "").trim();
            if (!exclusionName) {
                return;
            }

            const currentFilterModel = this.gridApi.getFilterModel() || {};
            const currentAMFilter = currentFilterModel["am.name"];
            const alreadyApplied = currentAMFilter &&
                Array.isArray(currentAMFilter.values) &&
                currentAMFilter.values.length === 1 &&
                currentAMFilter.values[0] === exclusionName;

            if (alreadyApplied) {
                return;
            }

            this.gridApi.setFilterModel({
                'am.name': {
                    type: 'set',
                    values: [exclusionName],
                },
            });
        },
        // Reseller/registrar/am/tld name→id lookups come straight from the
        // store (fetched once at login) — they only translate what the user
        // checks in the name-based filter boxes into ids for the request.
        // What the filter boxes *show* are the facets (see fetchFacets).
        fetchFilterOptions() {
            this.tldOptions = this.$store.state.tlds || [];
            this.amOptions = this.$store.state.ams || [];
            this.registrarOptions = this.$store.state.registrars || [];
            this.resellerOptions = this.$store.state.resellers || [];
        },
        // The filter params that determine the option lists: everything the
        // list request sends except sort, which doesn't change what matches.
        facetParams(state) {
            let params = this.buildFilterParams(state);
            delete params.sort_by;
            delete params.sort_dir;
            return params;
        },
        // Loads the option list for every Set Filter column under the given
        // filters (each column's own filter is excluded server-side, so its
        // checked values stay on offer). Resolves to true when `facets` was
        // updated, false when the request failed or a newer one overtook it.
        fetchFacets(params) {
            let key = JSON.stringify(params);
            let requestId = ++this.facetsRequestId;

            return this.$axiosSecure
                .get("/local-promos/filter-options", {
                    params: Object.assign({ expired: this.expired ? 1 : 0 }, params),
                })
                .then((response) => {
                    if (requestId !== this.facetsRequestId) {
                        return false;
                    }

                    this.facetsParamsKey = key;
                    this.facets = response.data || {};
                    return true;
                })
                .catch((error) => {
                    console.log(error);
                    return false;
                });
        },
        // Every filter dropdown depends on every other filter: after any
        // change, refetch the option lists for the filters now in force and
        // push them into the grid's Set Filters. Resolves to true when
        // narrowing changed what was checked somewhere (ag-grid then fires
        // filter-changed itself), false otherwise.
        syncFilterOptions() {
            if (!this.gridApi) {
                return Promise.resolve(false);
            }

            let params = this.facetParams();

            if (JSON.stringify(params) === this.facetsParamsKey) {
                return Promise.resolve(false);
            }

            return this.fetchFacets(params).then((loaded) => {
                if (!loaded) {
                    return false;
                }

                let changed = false;
                Object.keys(FILTER_PARAM_MAP).forEach((colId) => {
                    if (FILTER_PARAM_MAP[colId].facetKey) {
                        changed = this.applySetFilterValues(colId) || changed;
                    }
                });

                return changed;
            });
        },
        // What a column's Set Filter should currently offer, in the form the
        // filter displays (and keeps in its model): the facet as-is, or run
        // through valuesMap for columns that show a derived label.
        facetValues(colId) {
            let mapping = FILTER_PARAM_MAP[colId];
            let values = (mapping && this.facets[mapping.facetKey]) || [];

            if (mapping && mapping.valuesMap) {
                let displayByRaw = {};
                Object.keys(mapping.valuesMap).forEach((display) => {
                    displayByRaw[mapping.valuesMap[display]] = display;
                });
                values = values.map((v) =>
                    Object.prototype.hasOwnProperty.call(displayByRaw, v) ? displayByRaw[v] : v
                );
            }

            return values;
        },
        // Replaces a Set Filter's option list in place. Anything the user had
        // checked stays checked if it's still in the new list; if nothing
        // was checked, or nothing checked survived, everything is checked
        // (= no filter on that column). Returns whether the effective
        // selection changed — only then is ag-grid told to notify, which
        // re-fires filter-changed.
        applySetFilterValues(colId) {
            let instance = this.gridApi.getFilterInstance(colId);
            if (!instance || typeof instance.setFilterValues !== "function") {
                return false;
            }

            let options = this.facetValues(colId);
            let model = instance.getModel();
            let previous = model && Array.isArray(model.values) ? model.values : null;
            let kept = previous ? previous.filter((v) => options.includes(v)) : [];
            let changed = !!previous && kept.length !== previous.length;

            instance.setFilterValues(options, false, changed, kept.length ? kept : options);

            return changed;
        },
        // Persist search/filter/sort state so it survives this component being
        // unmounted and remounted (e.g. switching away from this tab and back).
        persistFilterState() {
            let state = {
                globalSearchTerm: this.globalSearchTerm,
                filterModel: this.gridApi ? (this.gridApi.getFilterModel() || {}) : {},
                sortModel: this.gridApi ? (this.gridApi.getSortModel() || []) : [],
            };

            try {
                sessionStorage.setItem(this.storageKey, JSON.stringify(state));
            } catch (e) {
                // Ignore storage errors (e.g. private browsing quota exceeded).
            }
        },
        readPersistedState() {
            try {
                return JSON.parse(sessionStorage.getItem(this.storageKey)) || null;
            } catch (e) {
                return null;
            }
        },
        restorePersistedState(saved) {
            if (!saved) {
                return;
            }

            if (saved.globalSearchTerm) {
                this.globalSearchTerm = saved.globalSearchTerm;
            }

            let hasFilterModel = saved.filterModel && Object.keys(saved.filterModel).length;
            let hasSortModel = Array.isArray(saved.sortModel) && saved.sortModel.length;

            if (this.gridApi && (hasFilterModel || hasSortModel)) {
                this.suppressGridEvents = true;

                if (hasFilterModel) {
                    this.gridApi.setFilterModel(saved.filterModel);
                }

                if (hasSortModel) {
                    this.gridApi.setSortModel(saved.sortModel);
                }

                this.$nextTick(() => {
                    this.suppressGridEvents = false;
                });
            }
        },
        // Builds the search/filter/sort query params shared by the paginated
        // list request, the download/export request and (minus sort) the
        // filter-options request, so all stay in sync with whatever's
        // currently applied in the grid. `state` ({globalSearchTerm,
        // filterModel, sortModel} — the persisted shape) overrides the live
        // grid state; used on mount to fetch option lists for a saved filter
        // state before the grid has it.
        buildFilterParams(state) {
            let params = {};
            let search = state ? state.globalSearchTerm : this.globalSearchTerm;

            if (search && search.trim()) {
                params.search = search.trim();
            }

            if (state || this.gridApi) {
                let sortModel = (state ? state.sortModel : this.gridApi.getSortModel()) || [];
                let filterModel = (state ? state.filterModel : this.gridApi.getFilterModel()) || {};

                if (sortModel.length) {
                    let sortColId = sortModel[0].colId;
                    let sortMapping = FILTER_PARAM_MAP[sortColId];
                    params.sort_by = sortMapping ? (sortMapping.sortKey || sortMapping.param) : sortColId;
                    params.sort_dir = sortModel[0].sort;
                }

                Object.keys(filterModel).forEach((colId) => {
                    let mapping = FILTER_PARAM_MAP[colId];
                    if (!mapping) {
                        return;
                    }

                    let model = filterModel[colId];
                    let value = null;

                    if (mapping.type === "text") {
                        value = model && model.filter;
                    } else if (mapping.type === "set-id") {
                        // Checkbox selections here are names (matching the
                        // column's own field) — resolve them to id(s) right
                        // before the request goes out, using the options
                        // list already fetched once on load.
                        let selectedNames = model && Array.isArray(model.values) ? model.values : [];
                        if (selectedNames.length) {
                            let ids = (this[mapping.optionsKey] || [])
                                .filter((o) => selectedNames.includes(String(o[mapping.nameKey])))
                                .map((o) => o.id);
                            value = ids.length ? ids.join(",") : null;
                        }
                    } else if (mapping.type === "set") {
                        let selectedValues = model && Array.isArray(model.values) ? model.values : [];
                        // A checked "(Blanks)" is null in the model, which
                        // join() would turn into an empty string — send the
                        // agreed token instead.
                        if (mapping.blankValue) {
                            selectedValues = selectedValues.map((v) => (v === null || v === "" ? mapping.blankValue : v));
                        }
                        // Some Set Filters show a formatted/derived value (e.g.
                        // list_based's Yes/No) rather than the raw column value —
                        // valuesMap translates back before it's sent to the API.
                        if (mapping.valuesMap) {
                            selectedValues = selectedValues.map((v) =>
                                Object.prototype.hasOwnProperty.call(mapping.valuesMap, v) ? mapping.valuesMap[v] : v
                            );
                        }
                        value = selectedValues.length ? selectedValues.join(",") : null;
                    } else if (mapping.type === "date") {
                        value = model && model.dateFrom ? model.dateFrom.split(" ")[0] : null;
                    }

                    if (value) {
                        params[mapping.param] = value;
                    }
                });
            }

            return params;
        },
        openLoadingInDiv() {
            this.$vs.loading({
                container: "#div-with-loading",
                scale: 0.8,
            });
        },
        hideLoadingInDiv() {
            this.$vs.loading.close("#div-with-loading > .con-vs-loading");
        },
        toggleColumnMovable(val) {
            if (val <= 576) {
                this.defaultColDef["suppressMovable"] = true;
            } else {
                this.defaultColDef["suppressMovable"] = false;
            }
        },
        toggleColumnPinned(val) {
            if (val <= 576) {
                this.maxPageNumbers = 4;
                this.gridOptions.columnApi.setColumnPinned("domain", null);
            } else {
                this.gridOptions.columnApi.setColumnPinned("domain", "left");
            }
        },
        onColumnResized() {
            this.gridApi.resetRowHeights();
        },
        sizeColumnsToFit() {
            this.gridApi.sizeColumnsToFit();
        },
        disableQueue() {
            this.rowClassRules = {
                "ag-row-disabled": 'data && (data.status == "edit pending")',
                "ag-checkbox-disabled":
                    'data && (data.status == "edit pending")',
                // "ag-row-red": 'data && (data.status == "expired")',
            };
        },
        // Refresh the page withou resetting filters
        refreshPage() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
            // Reset selection
            this.gridApi.deselectAll();
            // Reset sort
            this.gridApi.setSortModel(null);
            // Table Data AXIOS Call
            this.populateTableData();
        },
        // With filters for when we want to reset filters
        refreshPageWithFilters() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
            this.globalSearchTerm = "";

            try {
                sessionStorage.removeItem(this.storageKey);
            } catch (e) {
                // Ignore storage errors.
            }

            // Reset filters
            this.gridApi.setFilterModel(null);
            // Reset selection
            this.gridApi.deselectAll();
            // Reset sort
            this.gridApi.setSortModel(null);
            // Both resets above fire their grid events, which already queue
            // the (shared, debounced) option refresh + reload; this just
            // guarantees one runs even if neither event fires because
            // nothing was set. No direct populateTableData() here — that
            // made a second identical list request.
            this.scheduleReload();
        },
        populateTableData() {
            let self = this;

            let url = this.expired
                ? "/local-promos/expired"
                : "/local-promos";

            let params = Object.assign(
                {
                    page: this.currentPage,
                    limit: this.perPage,
                },
                this.buildFilterParams()
            );

            this.$axiosSecure
                .get(url, { params })
                .then((response) => {
                    let responseData = response.data;

                    // Backward compatibility with old array-only response shape.
                    if (Array.isArray(responseData)) {
                        this.tableData = responseData;
                        this.totalRows = responseData.length;
                        this.totalPagesFromApi = 0;
                    } else {
                        this.tableData = responseData.data || [];
                        this.totalRows = Number(
                            responseData.total ||
                            (responseData.meta && responseData.meta.total) ||
                            this.tableData.length
                        );

                        this.totalPagesFromApi = Number(responseData.last_page || 0);

                        if (responseData.per_page) {
                            this.perPage = Number(responseData.per_page);
                        }
                    }

                    this.flags.selectedAll = Array(this.totalPages || 1).fill(false);

                    if (this.gridApi) {
                        setTimeout(() => {
                            this.applyAmExclusionFilter();
                        }, 50);
                    }
                })
                .catch((error) => {
                    // Error notification
                    self.$vs.notify({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                    });

                    console.log(error);

                    // Clear Listing Count
                    //this.$store.dispatch("clearListingCount");
                });
        },
        gridSelectAll() {
            this.gridApi.forEachNodeAfterFilter((node) => {
                if (
                    !(
                        node.data.status == "new approved" ||
                        node.data.status == "rejected" ||
                        node.data.status == "error"
                    )
                ) {
                    node.setSelected(true);
                }
            });
        },
        gridDeselectAll() {
            this.gridApi.forEachNodeAfterFilter((node) => {
                node.setSelected(false);
            });
        },
        addPromo() {
            this.$router.push("/promos/add/cnic");
        },
        editPromo() {
            let extended = false;

            if (this.expired) {
                extended = true;
            }

            var selectedRowsData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            // console.log('selectedRowsData :>> ', selectedRowsData);
            this.$root.$emit(
                "selectedGroupEditInternal",
                selectedRowsData,
                extended
            );
        },
        duplicatePromo() {
            // console.log('selected', this.gridApi.getSelectedNodes());
            // console.log('selected data', this.gridApi.getSelectedNodes());
            var selectedRowsData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            // this.$root.$emit('selectedDuplicate', selectedRowsData);
            this.$root.$emit("duplicatePromoInternal", selectedRowsData);
        },
        deletePromo() {
            this.openLoadingInDiv();

            // console.log('selected', this.gridApi.getSelectedNodes());
            var selectedRowData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            var selectedRowId = selectedRowData.map((x) => x.id);

            // console.log("selectedRowId", selectedRowId);
            // return;

            this.$axiosSecure
                .post("/local-promos/delete", {
                    id: selectedRowId,
                })

                .then((response) => {
                    //console.log("success");
                    setTimeout(() => {
                        this.refreshPage();
                        //Hide loader
                        this.hideLoadingInDiv();
                        // Confimation notification
                        this.$vs.dialog({
                            color: "success",
                            title: "Deleted successfully",
                            text: "Selcted Internal Promos have been deleted.",
                            acceptText: "Close",
                            accept: () => {},
                        });
                    }, 100);
                })
                .catch((error) => {
                    console.log("error");
                    this.hideLoadingInDiv();
                    //Error msg
                    this.$vs.dialog({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                        acceptText: "Close",
                        accept: () => {},
                    });
                });
        },
        // Downloads the full filtered dataset from the backend (not just the
        // currently loaded page), applying whatever search/filter/sort is
        // currently active in the grid.
        downloadFilteredCsv() {
            let params = this.buildFilterParams();

            this.$vs.loading();

            this.$axiosSecure
                .get("/local-promos/download", { params, responseType: "blob" })
                .then((response) => {
                    let blob = new Blob([response.data], { type: "text/csv" });
                    let link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = "internal-promos.csv";
                    link.click();
                    URL.revokeObjectURL(link.href);
                })
                .catch((error) => {
                    this.$vs.notify({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                    });
                    console.log(error);
                })
                .finally(() => {
                    this.$vs.loading.close();
                });
        },
        toggleSelectAll() {
            if (this.flags["selectedAll"][this.currentPage - 1]) {
                this.gridDeselectAll();
                this.flags["selectedAll"][this.currentPage - 1] =
                    !this.flags["selectedAll"][this.currentPage - 1];
            } else {
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
                if (
                    this.selectedRowCount < 30 &&
                    this.selectedRowCount + this.perPage <= 30
                ) {
                    this.gridSelectAll();
                    this.flags["selectedAll"][this.currentPage - 1] =
                        !this.flags["selectedAll"][this.currentPage - 1];
                } else {
                    this.$vs.notify({
                        time: 5000,
                        color: "warning",
                        title: "Warning",
                        text: "You may select only upto 30 rows",
                    });
                }
            }
        },
        onFilterTextBoxChanged() {
            // The search text narrows the rows, so it narrows the option
            // lists too — same path as a column filter change.
            this.deselectAllSelectedRows();
            this.scheduleReload();
        },
        initSelectedAllFlag() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
        },
        deselectAllSelectedRows() {
            this.gridApi.deselectAll();

            this.flags["selectedAll"].fill(false);
        },
    },
    beforeMount() {
        this.disableQueue();

        // Reset movable columns on mobile
        this.toggleColumnMovable(this.windowWidth);

        (this.columnDefs = this.isBIUserRole
                ? [
                      {
                          headerName: "Client Name",
                          field: "new_reseller.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("new_reseller.name"));
                              },
                          },
                          minWidth: 200,
                          pinned: "left",
                        //   cellClass: "cellCustomWrap",
                        tooltipField: "new_reseller.name",
                          checkboxSelection: this.isLocalEditRule
                      },
                      {
                          headerName: "Type",
                          field: "type",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("type"));
                              },
                          },
                          minWidth: 160,
                          pinned: "left",
                      },
                      {
                          headerName: "TLD",
                          field: "tld.tld_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("tld.tld_name"));
                              },
                          },
                          minWidth: 100,
                          width: 150,
                          pinned: "left",
                      },
                    //   {
                    //       headerName: "CNIC ID",
                    //     //   field: "new_reseller.unique_registrar.new_registrar[0]['cnic_id']",
                    //       filter: "agSetColumnFilter",
                    //       minWidth: 150,
                    //       valueGetter: (data) => {
                    //         return data.data.new_reseller.unique_registrar.new_registrar && data.data.new_reseller.unique_registrar.new_registrar.length && data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id ? data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id : null
                    //       },
                    //       cellRenderer: (data) => {
                    //           return data.data.new_reseller.unique_registrar.new_registrar && data.data.new_reseller.unique_registrar.new_registrar.length && data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id ? data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id : null
                    //       }
                    //   },
                      {
                          headerName: "Registrar Name",
                          field: "new_reseller.unique_registrar.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("new_reseller.unique_registrar.name"));
                              },
                          },
                          minWidth: 175,
                          tooltipField: "new_reseller.unique_registrar.name"
                        //   cellClass: "cellCustomWrap",
                      },
                      {
                          headerName: "Status",
                          field: "status",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("status"));
                              },
                          },
                          cellRendererFramework: "CellRendererStatus",
                          minWidth: 150,
                      },
                      {
                          headerName: "AM",
                          field: "am.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("am.name"));
                              },
                          },
                          minWidth: 175,
                          width: 175,
                          suppressFilter: this.isAMExclusionActive() ? true : false
                      },
                      {
                          headerName: "Period (Yrs)",
                          field: "period",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("period"));
                              },
                          },
                          minWidth: 120,
                      },
                      {
                          headerName: "Effective 1Yr Price",
                          field: "effective_first",
                          filter: "agSetColumnFilter",
                          minWidth: 150,
                          valueGetter: (params) => {
                              if (params.data.type.includes("premium")) {
                                  return params.data.effective_first_per + "%";
                              } else {
                                  return (
                                      params.data.currency.sign +
                                      " " +
                                      params.data.effective_first
                                  );
                              }
                          },
                          cellRenderer: (data) => {
                              // console.log('data', data.data);
                              if (data.data.type.includes("premium")) {
                                  return (
                                      "<div>" +
                                      data.data.effective_first_per +
                                      "%</div>"
                                  );
                              } else {
                                  return (
                                      "<div>" +
                                      data.data.currency.sign +
                                      "  " +
                                      data.data.effective_first +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Effective Multi-Yr Price",
                          field: "effective_multi",
                          // filter: "agSetColumnFilter",
                          minWidth: 150,
                          cellRenderer: (data) => {
                              // this.number();
                              if (data.data.type.includes("premium")) {
                                  return (
                                      "<div>" +
                                      data.data.effective_multi_per +
                                      "%</div>"
                                  );
                              } else {
                                  return (
                                      "<div>" +
                                      data.data.currency.sign +
                                      "  " +
                                      data.data.effective_multi +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Retail Price",
                          field: "retail_price",
                          filter: "agSetColumnFilter",
                          minWidth: 150,
                          cellRenderer: (data) => {
                              // console.log('data', data.data);
                              if (data.data.retail) {
                                  return (
                                      "<div>" +
                                      data.data.retail.sign +
                                      "  " +
                                      data.data.retail_price +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Start Date",
                          field: "start_date",
                          filter: "agDateColumnFilter",
                          minWidth: 175,
                          filterParams: {
                              // Column-level filterParams replaces (not merges
                              // with) defaultColDef.filterParams, so these must
                              // be repeated here — without newRowsAction: "keep",
                              // ag-grid resets this filter's applied model on
                              // every rowData reload (i.e. every page change),
                              // dropping it from the next request. The backend
                              // (LocalPromoRepository) filters as a range match
                              // (start_date >= filters.start_date), so the
                              // client-side re-check ag-grid runs against the
                              // already server-filtered rows is locked to the
                              // matching "greaterThanOrEqual" operator instead of
                              // the "equals" default, which would otherwise wipe
                              // out rows the server correctly returned.
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              filterOptions: ["greaterThanOrEqual"],
                              comparator: function (
                                  filterLocalDateAtMidnight,
                                  cellValue
                              ) {
                                  let dateAsString = cellValue;
                                  if (dateAsString == null) return -1;
                                  let dateParts = dateAsString.split("-");
                                  let cellDate = new Date(
                                      Number(dateParts[0]),
                                      Number(dateParts[1]) - 1,
                                      Number(dateParts[2])
                                  );
                                  if (
                                      filterLocalDateAtMidnight.getTime() ===
                                      cellDate.getTime()
                                  ) {
                                      return 0;
                                  }
                                  if (cellDate < filterLocalDateAtMidnight) {
                                      return -1;
                                  }
                                  if (cellDate > filterLocalDateAtMidnight) {
                                      return 1;
                                  }
                              },
                              browserDatePicker: true,
                          },
                      },
                      {
                          headerName: "End Date",
                          field: "end_date",
                          filter: "agDateColumnFilter",
                          minWidth: 175,
                          filterParams: {
                              // See Start Date column: backend filters as
                              // end_date <= filters.end_date, so the client-side
                              // re-check is locked to "lessThanOrEqual" to match.
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              filterOptions: ["lessThanOrEqual"],
                              comparator: function (
                                  filterLocalDateAtMidnight,
                                  cellValue
                              ) {
                                  let dateAsString = cellValue;
                                  if (dateAsString == null) return -1;
                                  let dateParts = dateAsString.split("-");
                                  let cellDate = new Date(
                                      Number(dateParts[0]),
                                      Number(dateParts[1]) - 1,
                                      Number(dateParts[2])
                                  );
                                  if (
                                      filterLocalDateAtMidnight.getTime() ===
                                      cellDate.getTime()
                                  ) {
                                      return 0;
                                  }
                                  if (cellDate < filterLocalDateAtMidnight) {
                                      return -1;
                                  }
                                  if (cellDate > filterLocalDateAtMidnight) {
                                      return 1;
                                  }
                              },
                              browserDatePicker: true,
                          },
                      },
                      {
                          headerName: "Promo Tag",
                          field: "promotag.tag_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("promotag.tag_name"));
                              },
                          },
                          minWidth: 150,
                          tooltipField: "promotag.tag_name"
                        //   cellClass: "cellCustomWrap",
                      },
                      {
                          headerName: "Custom Tag",
                          field: "customtag.tag_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("customtag.tag_name"));
                              },
                          },
                          minWidth: 150,
                          tooltipField: "customtag.tag_name"
                        //   cellClass: "cellCustomWrap",
                          // cellRendererFramework: 'CellRendererCustomTagComments',
                      },
                      {
                          headerName: "Payment Mode",
                          field: "payment_mode",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("payment_mode"));
                              },
                          },
                          minWidth: 150,
                      },
                      {
                          headerName: "List Based",
                          field: "list_based",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("list_based"));
                              },
                          },
                          // Yes/No display derived here rather than mutating the
                          // raw "yes"/"no" row data — see FILTER_PARAM_MAP's
                          // valuesMap for how the selected Yes/No gets
                          // translated back for the API.
                          valueGetter: (data) => {
                              return data.data.list_based === "yes" ? "Yes" : "No";
                          },
                          cellRenderer: (data) => {
                              return data.data.list_based === "yes" ? "Yes" : "No";
                          },
                          minWidth: 150,
                          // hide:true
                      },
                      {
                          headerName: "List Frequency",
                          field: "list_frequency",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("list_frequency"));
                              },
                          },
                          minWidth: 150,
                          // hide:true
                      },
                      {
                          headerName: "Agreements",
                          field: "dropbox_detail",
                          filter: "agSetColumnFilter",
                          cellRendererFramework: "CellRendererAgreementList",
                          cellRendererParams: {
                              source: "localpromo",
                          },
                          minWidth: 130,
                      },
                      {
                          headerName: "Rebate Status",
                          field: "rebates_status",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("rebates_status"));
                              },
                          },
                          minWidth: 150,
                          cellRenderer: (data) => {
                              return data.data.rebates_status
                                  ? data.data.rebates_status
                                        .split("-")[0]
                                        .charAt(0)
                                        .toUpperCase() +
                                        data.data.rebates_status
                                            .split("-")[0]
                                            .slice(1) +
                                        " " +
                                        data.data.rebates_status.split("-")[1]
                                  : null;
                          },
                      },
                      {
                          headerName: "Comments",
                          field: "comments",
                          filter: "agTextColumnFilter",
                          minWidth: 150,
                          width: 100,
                          wrapText: true,
                          autoHeight: true,
                          tooltipField: "comments"
                        //   cellClass: "cellCustomWrap",
                      },
                  ]
                : [
                      {
                          headerName: "Client Name",
                          field: "new_reseller.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("new_reseller.name"));
                              },
                          },
                          minWidth: 200,
                          pinned: "left",
                          tooltipField: "new_reseller.name",
                        //   cellClass: "cellCustomWrap",
                          checkboxSelection: this.isLocalEditRule
                      },
                      {
                          headerName: "Type",
                          field: "type",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("type"));
                              },
                          },
                          minWidth: 160,
                          pinned: "left",
                      },
                      {
                          headerName: "TLD",
                          field: "tld.tld_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("tld.tld_name"));
                              },
                          },
                          minWidth: 100,
                          width: 150,
                          pinned: "left",
                      },
                      {
                          headerName: "Registrar Name",
                          field: "new_reseller.unique_registrar.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("new_reseller.unique_registrar.name"));
                              },
                          },
                          minWidth: 175,
                          tooltipField: "new_reseller.unique_registrar.name"
                        //   cellClass: "cellCustomWrap",
                      },
                      {
                          headerName: "Status",
                          field: "status",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("status"));
                              },
                          },
                          cellRendererFramework: "CellRendererStatus",
                          minWidth: 150,
                      },
                      {
                          headerName: "AM",
                          field: "am.name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("am.name"));
                              },
                          },
                          minWidth: 175,
                          width: 175,
                          suppressFilter: this.isAMExclusionActive() ? true : false
                      },
                      {
                          headerName: "Period (Yrs)",
                          field: "period",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("period"));
                              },
                          },
                          minWidth: 120,
                      },
                      {
                          headerName: "Effective 1Yr Price",
                          field: "effective_first",
                          filter: "agSetColumnFilter",
                          minWidth: 150,
                          valueGetter: (params) => {
                              if (params.data.type.includes("premium")) {
                                  return params.data.effective_first_per + "%";
                              } else {
                                  return (
                                      params.data.currency.sign +
                                      " " +
                                      params.data.effective_first
                                  );
                              }
                          },
                          cellRenderer: (data) => {
                              // console.log('data', data.data);
                              if (data.data.type.includes("premium")) {
                                  return (
                                      "<div>" +
                                      data.data.effective_first_per +
                                      "%</div>"
                                  );
                              } else {
                                  return (
                                      "<div>" +
                                      data.data.currency.sign +
                                      "  " +
                                      data.data.effective_first +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Effective Multi-Yr Price",
                          field: "effective_multi",
                          // filter: "agSetColumnFilter",
                          minWidth: 150,
                          cellRenderer: (data) => {
                              // this.number();
                              if (data.data.type.includes("premium")) {
                                  return (
                                      "<div>" +
                                      data.data.effective_multi_per +
                                      "%</div>"
                                  );
                              } else {
                                  return (
                                      "<div>" +
                                      data.data.currency.sign +
                                      "  " +
                                      data.data.effective_multi +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Retail Price",
                          field: "retail_price",
                          filter: "agSetColumnFilter",
                          minWidth: 150,
                          cellRenderer: (data) => {
                              // console.log('data', data.data);
                              if (data.data.retail) {
                                  return (
                                      "<div>" +
                                      data.data.retail.sign +
                                      "  " +
                                      data.data.retail_price +
                                      "</div>"
                                  );
                              }
                          },
                      },
                      {
                          headerName: "Start Date",
                          field: "start_date",
                          filter: "agDateColumnFilter",
                          minWidth: 175,
                          filterParams: {
                              // Column-level filterParams replaces (not merges
                              // with) defaultColDef.filterParams, so these must
                              // be repeated here — without newRowsAction: "keep",
                              // ag-grid resets this filter's applied model on
                              // every rowData reload (i.e. every page change),
                              // dropping it from the next request. The backend
                              // (LocalPromoRepository) filters as a range match
                              // (start_date >= filters.start_date), so the
                              // client-side re-check ag-grid runs against the
                              // already server-filtered rows is locked to the
                              // matching "greaterThanOrEqual" operator instead of
                              // the "equals" default, which would otherwise wipe
                              // out rows the server correctly returned.
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              filterOptions: ["greaterThanOrEqual"],
                              comparator: function (
                                  filterLocalDateAtMidnight,
                                  cellValue
                              ) {
                                  let dateAsString = cellValue;
                                  if (dateAsString == null) return -1;
                                  let dateParts = dateAsString.split("-");
                                  let cellDate = new Date(
                                      Number(dateParts[0]),
                                      Number(dateParts[1]) - 1,
                                      Number(dateParts[2])
                                  );
                                  if (
                                      filterLocalDateAtMidnight.getTime() ===
                                      cellDate.getTime()
                                  ) {
                                      return 0;
                                  }
                                  if (cellDate < filterLocalDateAtMidnight) {
                                      return -1;
                                  }
                                  if (cellDate > filterLocalDateAtMidnight) {
                                      return 1;
                                  }
                              },
                              browserDatePicker: true,
                          },
                      },
                      {
                          headerName: "End Date",
                          field: "end_date",
                          filter: "agDateColumnFilter",
                          minWidth: 175,
                          filterParams: {
                              // See Start Date column: backend filters as
                              // end_date <= filters.end_date, so the client-side
                              // re-check is locked to "lessThanOrEqual" to match.
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              filterOptions: ["lessThanOrEqual"],
                              comparator: function (
                                  filterLocalDateAtMidnight,
                                  cellValue
                              ) {
                                  let dateAsString = cellValue;
                                  if (dateAsString == null) return -1;
                                  let dateParts = dateAsString.split("-");
                                  let cellDate = new Date(
                                      Number(dateParts[0]),
                                      Number(dateParts[1]) - 1,
                                      Number(dateParts[2])
                                  );
                                  if (
                                      filterLocalDateAtMidnight.getTime() ===
                                      cellDate.getTime()
                                  ) {
                                      return 0;
                                  }
                                  if (cellDate < filterLocalDateAtMidnight) {
                                      return -1;
                                  }
                                  if (cellDate > filterLocalDateAtMidnight) {
                                      return 1;
                                  }
                              },
                              browserDatePicker: true,
                          },
                      },
                      {
                          headerName: "Promo Tag",
                          field: "promotag.tag_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("promotag.tag_name"));
                              },
                          },
                          minWidth: 150,
                          tooltipField: "promotag.tag_name",
                        //   cellClass: "cellCustomWrap",
                      },
                      {
                          headerName: "Custom Tag",
                          field: "customtag.tag_name",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("customtag.tag_name"));
                              },
                          },
                          minWidth: 150,
                          tooltipField: "customtag.tag_name",
                        //   cellClass: "cellCustomWrap",
                          // cellRendererFramework: 'CellRendererCustomTagComments',
                      },
                      {
                          headerName: "Payment Mode",
                          field: "payment_mode",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("payment_mode"));
                              },
                          },
                          minWidth: 150,
                      },
                      {
                          headerName: "List Based",
                          field: "list_based",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("list_based"));
                              },
                          },
                          // Yes/No display derived here rather than mutating the
                          // raw "yes"/"no" row data — see FILTER_PARAM_MAP's
                          // valuesMap for how the selected Yes/No gets
                          // translated back for the API.
                          valueGetter: (data) => {
                              return data.data.list_based === "yes" ? "Yes" : "No";
                          },
                          cellRenderer: (data) => {
                              return data.data.list_based === "yes" ? "Yes" : "No";
                          },
                          minWidth: 150,
                          // hide:true
                      },
                      {
                          headerName: "List Frequency",
                          field: "list_frequency",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("list_frequency"));
                              },
                          },
                          minWidth: 150,
                          // hide:true
                      },
                      {
                          headerName: "Agreements",
                          field: "dropbox_detail",
                          filter: "agSetColumnFilter",
                          cellRendererFramework: "CellRendererAgreementList",
                          cellRendererParams: {
                              source: "localpromo",
                          },
                          minWidth: 130,
                      },
                      {
                          headerName: "Rebate Status",
                          field: "rebates_status",
                          filter: "agSetColumnFilter",
                          filterParams: {
                              suppressAndOrCondition: true,
                              newRowsAction: "keep",
                              values: (params) => {
                                  params.success(this.facetValues("rebates_status"));
                              },
                          },
                          minWidth: 150,
                          cellRenderer: (data) => {
                              return data.data.rebates_status
                                  ? data.data.rebates_status
                                        .split("-")[0]
                                        .charAt(0)
                                        .toUpperCase() +
                                        data.data.rebates_status
                                            .split("-")[0]
                                            .slice(1) +
                                        " " +
                                        data.data.rebates_status.split("-")[1]
                                  : null;
                          },
                      },
                      {
                          headerName: "Comments",
                          field: "comments",
                          filter: "agTextColumnFilter",
                          minWidth: 150,
                          width: 100,
                          wrapText: true,
                          autoHeight: true,
                          tooltipField: "comments",
                        //   cellClass: "cellCustomWrap",
                      },
                      {
                          headerName: "CNIC ID",
                        //   field: "new_reseller.unique_registrar.new_registrar[0].cnic_id",
                          filter: "agSetColumnFilter",
                          minWidth: 150,
                          valueGetter: (data) => {
                            return data.data.new_reseller.unique_registrar.new_registrar && data.data.new_reseller.unique_registrar.new_registrar.length && data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id ? data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id : null
                          },
                          cellRenderer: (data) => {
                            return data.data.new_reseller.unique_registrar.new_registrar && data.data.new_reseller.unique_registrar.new_registrar.length && data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id ? data.data.new_reseller.unique_registrar.new_registrar[0].cnic_id : null
                          }
                      },
                  ]),
            // Set Grid sidebar
            (this.sideBar = {
                toolPanels: [
                    {
                        id: "columns",
                        labelDefault: "Columns",
                        labelKey: "columns",
                        iconKey: "columns",
                        toolPanel: "agColumnsToolPanel",
                        toolPanelParams: {
                            suppressRowGroups: true,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            suppressSideButtons: true,
                            suppressColumnSelectAll: true,
                            suppressColumnExpandAll: true,
                        },
                    },
                ],
            });
    },
    mounted() {
        this.gridApi = this.gridOptions.api;

        //agigrid loader
        this.gridApi.showLoadingOverlay();

        this.fetchFilterOptions();

        // Option lists must be in place before restoring/creating any Set
        // Filter instance — ag-grid grabs a Set Filter's `values` list
        // synchronously on first use, so restoring a saved filter model
        // before the lists load bakes in an empty option list for that
        // filter's lifetime. So: fetch the options for the saved state
        // first, then restore it, then load rows.
        let saved = this.readPersistedState();

        this.fetchFacets(this.facetParams(saved)).then(() => {
            // Restore search/filter/sort state persisted before this component
            // was last unmounted (e.g. switching away from this tab and back).
            this.restorePersistedState(saved);

            // Table Data AXIOS Call
            this.populateTableData();
        });

        // Resize to fit columns
        this.sizeColumnsToFit();

        // Reset pinned columns on mobile
        this.toggleColumnPinned(this.windowWidth);

        this.$root.$on("tableRefresh", () => {
            // console.log('refresh');
            this.refreshPage();
        });
    },
    destroyed() {
        // Persist the current filter/sort/search state before unmount — the
        // debounce timer may not have fired yet if the user switched tabs
        // right after changing a filter, which would otherwise silently
        // drop that change instead of saving it.
        this.persistFilterState();

        // Clear Listing Count
        this.$store.dispatch("clearListingCount");

        clearTimeout(this.searchDebounceTimer);

        // Unbind Button Event Listeners
        this.$root.$off("tableRefresh");
    },
};
</script>

<style lang="scss">
.ag-selection-checkbox {
    display: flex;
    align-items: center;
    height: 36px;
}

.ag-row-disabled,
.ag-checkbox-disabled .ag-group-checkbox {
    pointer-events: none;
}

.ag-row-disabled .ag-group-checkbox,
.ag-checkbox-disabled .ag-group-checkbox {
    opacity: 0.3;
}

.ag-row-level-1 .ag-group-value,
.text-capitalize input {
    text-transform: capitalize;
}

//Hide grouping arrow
.ag-group-expanded,
.ag-group-contracted {
    display: none !important;
}

.ag-group-checkbox {
    margin-left: 0 !important;
}

.text-capitalize {
    text-transform: capitalize;
}
.ag-row-red {
    background: #fff1ef;
}
</style>
